import { Disposable } from "aurelia-binding";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { RowItem } from "../../views/components/data-grid/data-grid";
import { Field } from "../../views/components/queries/field";
import * as moment from "moment";
import { LookupHierarchyControl } from "../../views/components/lookups/lookup-hierarchy-control/lookup-hierarchy-control";
import { LookupControl } from "../../views/components/lookups/lookup-control/lookup-control";
import { NumberFormatHelper } from "../../helpers/number-format-helper";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

export interface EntityMetaDataModel {
    uri: string;
    name: string;
    idPropertyName: string;
    entityType?: number;
    projectionProperties: EntityProjectionPropertyModel[];
    filterProperties: EntityFilterPropertyModel[];
    functions: SearchableEntityFunction[];
    joiningProperties: JoiningPropertyModel[];
}

export interface JoiningPropertyModel {
    friendlyName:string;
    joiningProperty:string;
    joiningVetContextProperty:string;
}

export interface SearchableEntityFunction {
    entityUri: string;
    functionName: string;
    functionDisplayName: string;
    parameters: SearchableEntityFunctionParameter[];
}

export interface SearchableEntityFunctionParameter {
    name: string;
    type: number;
    parameter: any;
    defaultValue: string;
    isReadonly: boolean;
}

export interface EntityProjectionPropertyModel {
    name: string;
    friendlyName: string;
    showByDefault: boolean;
    type: DataType;
    format: string;
    isNullable: boolean;
    defaultOrdering: ProjectionPropertyOrdering;
    isDynamicProperty: boolean;
    isCollection: boolean;
    filterPropertyName?: string;
}

export enum ProjectionPropertyOrdering {
    None,
    Ascending,
    Descending
}

export enum QueryAssignmentEnum {
    User = 0,
    Role = 1,
    Department = 2,
    Branch = 3,
    Global = 4
}

export interface AndOrModel {
    name: string;
    isAnd: boolean;
}

export interface EntityFilterPropertyModel {
    name?: string;
    friendlyName?: string;
    type?: DataType;
    lookupMappedToProperty?: string;
    lookupParametersMappedToProperty?: string;
    entityFinderMappedToProperty?: string;
    isNullable: boolean;
    isDynamicProperty: boolean;
    startLabel?: string;
    endLabel?: string;
    isUpdatable?: boolean;
    isCollection?: boolean;
    tags?: string;
    format?: string;
}

export interface EntityMetaDataModelOperator {
    friendlyName?: string;
    translatedSymbol: string;
    supportedTypes?: DataType[];
    supportedTypesForCollectionProperties?: DataType[];
    requiresInterpolation?: boolean;
    isOnlyForNullableProperties?: boolean;
    requiresFilterValue?: boolean;
}

export interface DataGridColumn {
    isFilterable?: boolean;
    //header column tooltip
    tooltipForTitle?: string;
    tooltipForCell?: string | ((item: any) => string);
    title?: string;
    // type of the column as string , number or boolean needed for UI representation of the column(boolean will be a checkbox , etc)
    dataType?: DataType | ((item: any) => DataType);
    // header column
    name: string;
    // indicate if the column can be edited or not
    isEditable?: boolean;
    readOnly?: boolean | ((item: any) => boolean);
    // indicates if this column can be sorted or not
    isSortable?: boolean;
    sortFunction?: (aItem: any, bItem: any, columnName: string) => number;
    isGrouping?: boolean;
    // indicates if this column is visible in the UI
    isHidden?: boolean | ((item: any) => boolean);
    // value of the column(so a function or a lookup query or some text that acts as a filter)
    value?: any;
    // parameter for lookup(a query) or for a function
    parameter?: any;
    // second parameter that depends on the context in which is used
    // (for buttons indicated the icon to show when the sub content is expanded)
    secondParameter?: any;
    thirdParameter?: any;
    // indicates the sorting order
    sortOrder: number;
    groupOrder?: number;
    // indicates the position of the column respect to the others
    columnOrder: number;
    // indicates if the sorting is ascending or descending
    isAscending?: boolean;
    // indicates if the first click to sort should be ascending or descending
    isDescendingOnFirstSortingTriggered?: boolean;
    // filter applied to the column
    filters?: QueryFilter[];
    // this is a function to will return a certain number of filters based on the filterValues
    filtersResolver?: (filters?: QueryFilter[]) => QueryFilter[];
    valueConverter?: (value, item: any, params?: any) => any;
    getComponentClass?: (item: any) => string;
    getCellInlineStyles?: (item: any) => string;
    // indicate if this column must be created per each element
    instancePerRow?: boolean;
    // this is used for footer of the datagrid for instance summation of a column
    getFooter?: (collection: any[]) => any;
    // optional width will over write the widths from ItemSource
    width?: string;
    getCellClass?: (item: any) => string;
    includeInExcel?: boolean;
    disableSorting?: boolean;
    isDynamicProperty?: boolean;
    isCollection?: boolean;
    isNullable?: boolean;
    selectDefaultOnEdit?: boolean | ((item) => boolean);
    filterActive?: boolean;
    isApplied?: boolean;
    mobileDescription?: string;
    fieldTitle?: string;
    mobileTitle?: string;
    subText?: string;
    ghgSelectedItems?: [];
    items?: [];
    filterOpen?: boolean;
    previousLabel?: string;
    //Is column applied on mobile filter
    isAppliedMobile?: boolean;
    originalValue?: any;
    skipCollate?: boolean;
}

export interface DataGridColumnOverride {
    property?: string;
    valueConverter?: (value, item: any, params?: any) => any;
    getComponentClass?: (item: any) => string;
    getCellInlineStyles?: (item: any) => string;
    getCellClass?: (item: any) => string;
    titleToolTip?: string;
}

export class DataGridColumnHelper {

    public static applyDataGridColumnOverride(column: DataGridColumn, columnOverride: DataGridColumnOverride) {
        if (columnOverride) {
            column.valueConverter = columnOverride.valueConverter || column.valueConverter;
            column.getComponentClass = columnOverride.getComponentClass || column.getComponentClass;
            column.getCellInlineStyles = columnOverride.getCellInlineStyles || column.getCellInlineStyles;
            column.tooltipForTitle = columnOverride.titleToolTip || column.tooltipForTitle;
            column.getCellClass = columnOverride.getCellClass || column.getCellClass;
        }

        if ((column.dataType == DataType.Date || column.dataType == DataType.DateTime) && !column.valueConverter) {
            column.valueConverter = value => {
                if (!value) {
                    return "";
                }

                if (column.dataType == DataType.DateTime) {
                    return moment(value).format(column.secondParameter);
                } else {
                    return moment.utc(value).format(column.secondParameter);
                }
            }
        }

        if ((column.dataType == DataType.Number || column.dataType == DataType.Double) && !column.valueConverter) {
            //Example for names like EntityId etc.
            const isIdentifier = column.name.endsWith("Id");
            if (!isIdentifier) {
                column.valueConverter = (value, item) => {
                    const decimalPlaces = typeof column.thirdParameter === "function"
                        ? column.thirdParameter(item)
                        : column.thirdParameter;

                    return value ? NumberFormatHelper.getFormattedValue(value, null, column.dataType == DataType.Number ? 0 : decimalPlaces) : value;
                }
            }
        }
    }
}

export interface SortProperty {
    propertyName: string;
    order: ProjectionPropertyOrdering;
}

export class QueryFilter {
    // public selectedModel?: EntityMetaDataModel;
    public filterOperator: EntityMetaDataModelOperator;
    public filterValue: any = undefined;
    public isAnd?: boolean;
    public aggregateOperator?: number;
    public joiningClause?: JoiningPropertyModel;
    public isLast?: boolean;
    public isReadonly?: boolean;
    public uri?: string;
    public isAggregate?: boolean;
    public selectedForGrouping?: boolean;
    public groupColor?: string;
    public groupGuid?: string;
    public subGroupGuid?: string;
    public filterProperty: EntityFilterPropertyModel;
    public orderByProperty?: EntityFilterPropertyModel;
    public skipOnNullOrEmpty: boolean = true;
    public subscription?: Disposable;
    public filterContextPropertyValue?: any;
    public previousValue?: any;
    public originalValue?: any;

    public static getQuery(propertyName: string,
        value: any,
        symbolOperator,
        type: DataType,
        isAnd: boolean = false,
        isLast: boolean = false,
        groupGuid: string = null,
        selectedForGrouping: boolean = false,
        requiresInterpolation: boolean = false): QueryFilter {
        return <QueryFilter>{
            filterProperty: { name: propertyName, type },
            filterOperator: { translatedSymbol: symbolOperator, requiresInterpolation: requiresInterpolation, requiresFilterValue: true },
            filterValue: value,
            isAnd,
            isLast,
            groupColor: null,
            groupGuid,
            selectedForGrouping,
            skipOnNullOrEmpty: true
        };
    }

    public static clone(x: QueryFilter): QueryFilter {
        const clonedCopy = CloneHelper.clone(x);
        const result: QueryFilter = new QueryFilter();
        result.filterOperator = clonedCopy.filterOperator;
        result.filterValue = clonedCopy.filterValue;
        result.isAnd = clonedCopy.isAnd;
        result.isLast = clonedCopy.isLast;
        result.selectedForGrouping = clonedCopy.selectedForGrouping;
        result.groupColor = clonedCopy.groupColor;
        result.groupGuid = clonedCopy.groupGuid;
        result.filterProperty = clonedCopy.filterProperty;
        result.filterContextPropertyValue = clonedCopy.filterContextPropertyValue;
        result.uri = clonedCopy.uri;

        return result;
    }
}

export interface QueryCategory {
    id: number;
    categoryName: string;
}

export enum QueryCategoryEnum {
    MyQueries = 1,
    SharedQueries = 2
}

export class ItemSource {

    //The Source Type is the type of the source as an ODataUrl or an Array or something else.
    sourceType: SourceType;

    //This is the raw source that can be a string url or a collection
    source: any | any[];

    //these are the columns may be null
    columns?: DataGridColumn[];

    //these are the initial filters applied to the collection
    initialFilters?: QueryFilter[];

    //these are the initial filters applied to the collection
    initialSorting?: DataGridColumn[];

    widths?: { [column: string]: string };

    groupInitialFilters?: boolean;

    limit?: number;

    propertyDefaults?: { [propertyName: string]: any };

    filterFunc?: any;

    getRowClass?: (item: any, rowItem: RowItem) => string;

    getGroupName?: (columnd: DataGridColumn[], item: any) => string;
}

export enum SourceType {
    Collection,
    ODataUrl
}

export enum DataType {
    Default = 1,
    Boolean = 2,
    Number = 3,
    Double = 4,
    Color = 5,
    Image = 6,
    Date = 7,
    Lookup = 8,
    HierarchicalLookup = 9,
    Icon = 10,
    CustomTemplate = 11,
    DateTime = 12,
    Password = 13,
    Button = 15,
    Expander = 16,
    ComboBox = 17,
    Radio = 18,
    Time = 19,
    EntityFinder = 20,
    //DO NOT REMOVE THESE DURING MERGE FROM GENESIS
    RangeSelector = 1000,
    CheckedLookupSelector = 1001,
    CheckedListSelector = 1002,
    RatingSelector = 1003,
    GHGRatingSelector = 1004,
    MultiLookupSelector = 1005,
    DateRange = 1006,
    Many2Many = 1007
}

export class SavedQueriesDataModel {
    public name: string;
    public queryFilters?: QueryFilter[];
    public queryId?: number;
    public createdByCurrentUser: boolean;
    public dateCreated?: Date;
    public columns?: DataGridColumn[];

    constructor(name: string,
        queryFilters: Array<QueryFilter>,
        queryId: number,
        dateCreated: Date,
        columns: DataGridColumn[],
        createdByCurrentUser: boolean) {
        this.name = name;
        this.queryFilters = queryFilters;
        this.queryId = queryId;
        this.dateCreated = dateCreated;
        this.columns = columns;
        this.createdByCurrentUser = createdByCurrentUser;
    }
}

export interface PersistQueryRequest {
    queryId: number;
    queryName: string;
    description: string;
    queryBody: string;
    columns: string;
    assignmentsToAdd: QueryAssignmentBulkItem;
    assignmentsToRemove: QueryAssignmentBulkItem;
}

export interface QueryAssignmentBulkItem {
    queryId: number;
    userQueryAssignments: AssignmentItem[];
    roleQueryAssignments: AssignmentItem[];
    departmentQueryAssignments: AssignmentItem[];
    branchQueryAssignments: AssignmentItem[];
    assignGlobally: boolean;
    assignGlobalDefault: boolean;
}

export interface AssignmentItem {
    id: number;
    isDefault: boolean;
}

export interface AssignedQueryItem {
    query: Query;
    creatorFullName: string;
}

export interface QueryCategoryLookupsModel {
    unsavedBranchesLookupVMs: LookupHierarchyControl[];
    unsavedDepartmentsLookupVMs: { branchLookup: LookupHierarchyControl, departmentLookup: LookupControl }[];
    unsavedRolesLookupVMs: { branchLookup: LookupHierarchyControl, departmentLookup: LookupControl, roleLookup: LookupControl }[];
    unsavedUsersLookupVMs: { branchLookup: LookupHierarchyControl, departmentLookup: LookupControl, roleLookup: LookupControl, userLookup: LookupControl }[];
}

export class Query {
    public queryId: number;
    public queryName: string;
    public description: string;
    public createdDate: Date;
    public queryBody: string;
    public columns: string;
    public createdByCurrentUser: boolean;
    public creatorFullName: string;

    constructor(queryId: number,
        queryName: string,
        description: string,
        createdDate: Date,
        queryBody: string,
        columns: string,
        creatorFullName: string,
        createdByCurrentUser?: boolean) {
        this.queryId = queryId;
        this.queryName = queryName;
        this.description = description;
        this.createdDate = createdDate;
        this.queryBody = queryBody;
        this.columns = columns;
        this.creatorFullName = creatorFullName;
        this.createdByCurrentUser = createdByCurrentUser ? createdByCurrentUser : this.createdByCurrentUser;
    }
}

export interface GetQueriesResponse {
    queries: Query[];
    defaultQueryId: number;
    currentUserFullName: string;
}

export interface GetAssignedQueriesResponse {
    queries: AssignedQueryItem[];
}

export interface PersistQueryResponse {
    savedQueryId: number;
}

export interface UpdateODataResponse {
    isSuccess: boolean;
    messages: string[];
}

export interface GetOperatorsResponse {
    operatorsForSingleValues: { [index: string]: Array<EntityMetaDataModelOperator> };
    operatorsForCollections: { [index: string]: Array<EntityMetaDataModelOperator> };
}

export interface CloneQueryResponse {
    query: Query;
}

export interface AddQueryAssignmentResponse {
    isSuccess: boolean;
}

export interface GetQueryAssignmentsResponse {
    branchItems: AssignmentItem[];
    departmentItems: AssignmentItem[];
    roleItems: AssignmentItem[];
    userItems: AssignmentItem[];
    sharedGlobally: boolean;
    sharedDefaultGlobally: boolean;
}

export interface DeleteAssignmentsForQueryResponse {
    isSuccess: boolean;
}

export interface DeleteAssignmentsForQueryRequest {
    queryId: number;
}

export interface DeleteQueryResponse {
    isSuccess: boolean;
}

export interface DeleteQueryRequest {
    queryId: number;
}

export interface GetQueryAssignmentsRequest {
    queryId: number;
}

export interface AddQueryAssignmentRequest {
    queryId: number;
    categoryId: number;
    category: QueryAssignmentEnum;
    isDefault: boolean;
}

export interface CloneQueryRequest {
    queryId: number;
    queryName: string;
    shareGlobally: boolean;
}

export class GetQueriesRequest {
    entityType: string;

    constructor(entityType: string) {
        this.entityType = entityType;
    }
}

export class GetAllQueriesRequest {
    entityType: string[];

    constructor(entityType: string[]) {
        this.entityType = entityType;
    }
}

export interface UpdateODataRequest {
    selectedIds: number[];
    fields: Field[];
}

export interface ODataResponse<T> {
    "@odata.context": string;
    "@odata.count": number;
    value: T[];
}

export class QueryColumnHelper {
    public static generateDefaultColumn(property: EntityProjectionPropertyModel, model: EntityMetaDataModel): DataGridColumn {
        const column: DataGridColumn = {
            name: property.name,
            title: property.friendlyName,
            dataType: property.type,
            isHidden: !property.showByDefault,
            isFilterable: true,
            isSortable: false,
            isGrouping: false,
            sortOrder: 0,
            groupOrder: 0,
            columnOrder: 0
        };
        if (
            column.isAscending == null &&
            property.defaultOrdering == ProjectionPropertyOrdering.Ascending
        ) {
            column.isAscending = true;
            column.isSortable = true;
        }
        if (
            column.isAscending == null &&
            property.defaultOrdering == ProjectionPropertyOrdering.Descending
        ) {
            column.isAscending = false;
            column.isSortable = true;
        }
        if ((property.type === DataType.Date || property.type === DataType.DateTime) && !column.valueConverter) {
            column.valueConverter = QueryColumnHelper.getDateFilterFunc(property);
            column.secondParameter = property.format;
        }
        column.isDynamicProperty = property.isDynamicProperty;
        column.isCollection = property.isCollection;

        const projectedProperty = model.projectionProperties.filter(x => x.name == column.name)[0];
        const filterProperty = model.filterProperties.filter(x => x.name == column.name)[0];
        if (filterProperty && projectedProperty && filterProperty.lookupMappedToProperty) {
            column.parameter = filterProperty.lookupMappedToProperty;
        }

        if (filterProperty && projectedProperty && filterProperty.lookupParametersMappedToProperty) {
            column.secondParameter = filterProperty.lookupParametersMappedToProperty;
        }

        if (filterProperty && projectedProperty && filterProperty.entityFinderMappedToProperty) {
            column.parameter = filterProperty.entityFinderMappedToProperty;
        }

        const filterPropertyLookup = model.filterProperties.find(x =>
            (projectedProperty.filterPropertyName && x.name.toLowerCase() == projectedProperty.filterPropertyName.toLowerCase()) ||
            x.name.replace("Id", "").toLowerCase() == column.name.toLowerCase() &&
            x.type == DataType.Lookup &&
            column.dataType == DataType.Default);

        if (filterPropertyLookup) {
            if (!column.filters) {
                column.filters = [];
            }
            column.filters.push((<QueryFilter>{
                filterOperator: <EntityMetaDataModelOperator>{ translatedSymbol: "eq", requiresFilterValue: true },
                filterProperty: <EntityFilterPropertyModel>{
                    name: filterPropertyLookup.name,
                    isDynamicProperty: filterPropertyLookup.isDynamicProperty,
                    type: filterPropertyLookup.type,
                    lookupMappedToProperty: filterPropertyLookup.lookupMappedToProperty,
                    lookupParametersMappedToProperty: filterPropertyLookup.lookupParametersMappedToProperty
                },
                filterValue: null,
                isAnd: true,
                skipOnNullOrEmpty: true
            }));
        }

        const filterPropertyEntityFinderLookup = model.filterProperties.find(x =>
            (projectedProperty.filterPropertyName && x.name.toLowerCase() == projectedProperty.filterPropertyName.toLowerCase()) ||
            x.name.replace("Id", "").toLowerCase() == column.name.toLowerCase() &&
            x.type == DataType.EntityFinder &&
            column.dataType == DataType.Default);

        if (filterPropertyEntityFinderLookup) {
            if (!column.filters) {
                column.filters = [];
            }
            column.filters.push((<QueryFilter>{
                filterOperator: <EntityMetaDataModelOperator>{ translatedSymbol: "eq", requiresFilterValue: true },
                filterProperty: <EntityFilterPropertyModel>{
                    name: filterPropertyEntityFinderLookup.name,
                    isDynamicProperty: filterPropertyEntityFinderLookup.isDynamicProperty,
                    type: filterPropertyEntityFinderLookup.type,
                    entityFinderMappedToProperty: filterPropertyLookup.entityFinderMappedToProperty
                },
                filterValue: null,
                isAnd: true,
                skipOnNullOrEmpty: true
            }));
        }

        return column;
    }

    public static getDateFilterFunc(property: EntityProjectionPropertyModel) {
        return value => {
            if (!value) {
                return "";
            }
            const format = property.format ? property.format : (property.type == DataType.DateTime ? 
                ConfigService.instance().config.common.defaultDateTimeFormat : ConfigService.instance().config.common.defaultDateFormat);
            if (property.type == DataType.DateTime) {
                return moment(value).format(format);
            } else {
                return moment.utc(value).format(format);
            }
        }
    }

    public static addFilteringToColumns(filterColumns: DataGridColumn[]) {
        filterColumns.forEach(column => {
            if (!(column.filters && column.filters.length > 0)) {
                column.filters = [];

                switch (column.dataType) {
                    case DataType.Default:
                    case DataType.Number:
                    case DataType.Double:
                        // add filtering
                        column.filters.push((<QueryFilter>{
                            filterOperator: <EntityMetaDataModelOperator>{
                                requiresInterpolation: DataType.Default === column.dataType,
                                translatedSymbol: this.getDefaultTranslatedSymbol(column.dataType),
                                friendlyName: this.getDefaultOperatorFriendlyName(column.dataType),
                                requiresFilterValue: true
                            },
                            filterProperty: <EntityFilterPropertyModel>{
                                name: column.name,
                                isDynamicProperty: column.isDynamicProperty,
                                lookupMappedToProperty: null,
                                type: column.dataType
                            },
                            filterValue: null,
                            isAnd: true,
                            skipOnNullOrEmpty: true
                        }));
                        break;
                    case DataType.Boolean:
                        // add filtering
                        column.filters.push((<QueryFilter>{
                            filterOperator: <EntityMetaDataModelOperator>{
                                translatedSymbol: this.getDefaultTranslatedSymbol(column.dataType),
                                friendlyName: this.getDefaultOperatorFriendlyName(column.dataType),
                                requiresFilterValue: true
                            },
                            filterProperty: <EntityFilterPropertyModel>{ name: column.name, isDynamicProperty: column.isDynamicProperty, type: column.dataType },
                            filterValue: undefined,
                            isAnd: true
                        }));
                        break;
                    case DataType.Date:
                    case DataType.DateTime:
                        // add filtering
                        column.filters.push((<QueryFilter>{
                            filterOperator: <EntityMetaDataModelOperator>{
                                translatedSymbol: "ge",
                                friendlyName: ">=",
                                requiresFilterValue: true
                            },
                            filterProperty: <EntityFilterPropertyModel>{ name: column.name, isDynamicProperty: column.isDynamicProperty, type: column.dataType },
                            filterValue: null,
                            isAnd: true,
                            skipOnNullOrEmpty: true
                        }));
                        // add filtering
                        column.filters.push((<QueryFilter>{
                            filterOperator: <EntityMetaDataModelOperator>{
                                translatedSymbol: "le",
                                friendlyName: "<=",
                                requiresFilterValue: true
                            },
                            filterProperty: <EntityFilterPropertyModel>{ name: column.name, isDynamicProperty: column.isDynamicProperty, type: column.dataType },
                            filterValue: null,
                            isAnd: true,
                            skipOnNullOrEmpty: true
                        }));
                        break;
                    case DataType.Lookup:
                    case DataType.HierarchicalLookup:
                        column.filters.push((<QueryFilter>{
                            filterOperator: <EntityMetaDataModelOperator>{
                                translatedSymbol: "eq",
                                friendlyName: "=",
                                requiresFilterValue: true
                            },
                            filterProperty: <EntityFilterPropertyModel>{ name: column.name, isDynamicProperty: column.isDynamicProperty, type: column.dataType, lookupMappedToProperty: column.parameter },
                            filterValue: null,
                            isAnd: true,
                            skipOnNullOrEmpty: true
                        }));
                        break;
                    case DataType.EntityFinder:
                        column.filters.push((<QueryFilter>{
                            filterOperator: <EntityMetaDataModelOperator>{
                                translatedSymbol: "eq",
                                friendlyName: "=",
                                requiresFilterValue: true
                            },
                            filterProperty: <EntityFilterPropertyModel>{ name: column.name, isDynamicProperty: column.isDynamicProperty, type: column.dataType, entityFinderMappedToProperty: column.parameter },
                            filterValue: null,
                            isAnd: true,
                            skipOnNullOrEmpty: true
                        }));
                        break;
                    case DataType.ComboBox:
                        column.filters.push((<QueryFilter>{
                            filterOperator: <EntityMetaDataModelOperator>{
                                requiresInterpolation: false,
                                translatedSymbol: this.getDefaultTranslatedSymbol(column.dataType),
                                friendlyName: this.getDefaultOperatorFriendlyName(column.dataType),
                                requiresFilterValue: true
                            },
                            filterProperty: <EntityFilterPropertyModel>{ name: column.name, isDynamicProperty: column.isDynamicProperty, type: column.dataType },
                            filterValue: null,
                            isAnd: true,
                            skipOnNullOrEmpty: true
                        }));
                }
            }
        });
    }

    public static getDefaultOperatorFriendlyName(type: DataType): string {
        switch (type) {
            case DataType.Default:
                return "contains";
        }
        return "=";
    }

    public static getDefaultTranslatedSymbol(type: DataType): string {
        switch (type) {
            case DataType.Default:
                return "contains({0}, '{1}')";
            case DataType.Number:
                return "eq";
            case DataType.Boolean:
                return "eq";
            case DataType.Date:
            case DataType.DateTime:
                return "eq";
        }
        return "eq";
    }

}
